import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useCart } from '@src/context/CartContext'
import { usePublicFeaturedProducts } from '@src/hooks/queries/useFeaturedProducts'
import { usePublicLiteratureForProduct } from '@src/hooks/queries/useLiterature'
import { useRelatedProducts } from '@src/hooks/queries/useProducts'
import { isFeaturedProduct } from '@src/types'
import type { CatalogProduct, FeaturedProduct, ProductLiterature } from '@src/types'
import { AppModal } from '@src/components/AppModal'
import PdfPreviewModal from '@src/components/PdfPreviewModal'
import ImageLightboxModal from '@src/components/ImageLightboxModal'
import SaleCornerBadge from '@src/components/SaleCornerBadge'
import { getProductImage, handleImageError, PLACEHOLDER_IMAGE } from '@src/utils/productImages'
import {
  PrimaryButton,
  WhiteButton,
  colors
} from '@src/theme/sharedComponents'

interface ProductFlyerModalProps {
  product: (CatalogProduct | FeaturedProduct) | null
  onClose: () => void
  isAuthenticated: boolean
  isAdmin?: boolean
  onLoginRequired?: () => void
  categoryImageMap?: Record<string, string>
}

const ProductLayout = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 575px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`

const ImageContainer = styled.div`
  flex: 0 0 200px;
  height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${colors.backgroundAlt};
  border: 1px solid ${colors.borderLight};
  border-left: 3px solid ${colors.primary};
  padding: 1.5rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 575px) {
    flex: none;
    height: 200px;
    padding: 1rem;
  }
`

const ProductImage = styled.img`
  width: 100%;
  height: 160px;
  object-fit: contain;
`

const CarouselArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255,255,255,0.85);
  border: 1px solid ${colors.borderLight};
  border-radius: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.7rem;
  color: ${colors.textLightMode};
  padding: 0;
  z-index: 1;
  transition: background 0.15s;

  &:hover { background: white; }
  &.prev { left: 0; }
  &.next { right: 0; }
`

const CarouselDots = styled.div`
  display: flex;
  gap: 0.35rem;
  margin-top: 0.6rem;
`

const Dot = styled.span<{ $active: boolean }>`
  width: 5px;
  height: 5px;
  background: ${({ $active }) => $active ? colors.primary : colors.borderLight};
  transition: background 0.15s;
`

const ProductInfo = styled.div`
  flex: 1;
`

const PriceWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
`

const Price = styled.span`
  font-size: 2.1rem;
  font-weight: 700;
  font-family: 'Barlow Condensed', sans-serif;
  letter-spacing: 0.02em;
  color: ${colors.primaryDark};
`

const OriginalPrice = styled.span`
  font-size: 1.2rem;
  font-weight: 500;
  color: ${colors.textLightModeMuted};
  text-decoration: line-through;
`

const SavingsBadge = styled.span`
  background: ${colors.primary};
  color: white;
  padding: 0.25rem 0.6rem;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: 'Barlow Condensed', sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`

const MfrName = styled.span`
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  font-family: 'Barlow Condensed', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${colors.textLightModeMuted};
  margin-bottom: 0.5rem;
`

const PromoBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(0, 166, 118, 0.08);
  color: ${colors.accent};
  border: 1px solid rgba(0, 166, 118, 0.3);
  border-left: 3px solid ${colors.accent};
  padding: 0.25rem 0.6rem;
  font-size: 0.78rem;
  font-weight: 600;
  font-family: 'Barlow Condensed', sans-serif;
  letter-spacing: 0.04em;
  margin-bottom: 0.75rem;
`

const Description = styled.p`
  color: ${colors.textLightMode};
  font-size: 0.975rem;
  line-height: 1.6;
  margin-bottom: 1rem;
`

const Details = styled.p`
  color: ${colors.textLightModeMuted};
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 0;
`

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  margin-top: 1.5rem;
  border-top: 2px solid ${colors.primary};

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
`

const SpecItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid ${colors.borderLight};
  border-right: 1px solid ${colors.borderLight};

  &:nth-child(2n) { border-right: none; }

  i {
    color: ${colors.primary};
    width: 18px;
    flex-shrink: 0;
  }

  strong {
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.78rem;
    color: ${colors.textLightModeMuted};
    white-space: nowrap;
  }

  span { color: ${colors.textLightMode}; }
`

const ActionButton = styled(PrimaryButton)`
  flex: 1;
  justify-content: center;

  @media (max-width: 575px) { width: 100%; }
`

const RelatedSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid ${colors.primary};
`

const RelatedTitle = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  font-family: 'Barlow Condensed', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${colors.textLightModeMuted};
  margin-bottom: 0.75rem;
`

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
`

const RelatedCard = styled.button`
  background: ${colors.backgroundAlt};
  border: 1px solid ${colors.borderLight};
  border-left: 3px solid transparent;
  padding: 0.75rem;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s;

  &:hover { border-left-color: ${colors.primary}; }

  img {
    width: 100%;
    height: 60px;
    object-fit: contain;
    margin-bottom: 0.4rem;
  }
`

const RelatedName = styled.p`
  font-size: 0.75rem;
  color: ${colors.textLightMode};
  margin: 0 0 0.2rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const RelatedPrice = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  font-family: 'Barlow Condensed', sans-serif;
  color: ${colors.primaryDark};
  margin: 0;
`

const OrderHistoryLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${colors.primary};
  font-size: 0.82rem;
  font-weight: 600;
  font-family: 'Barlow Condensed', sans-serif;
  letter-spacing: 0.04em;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover { color: ${colors.primaryDark}; }
`

const LitDropdownWrapper = styled.div`
  position: relative;
  flex: 1;
`

const LitDropdownMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 0.4rem);
  left: 0;
  right: 0;
  background: ${colors.background};
  border: 1px solid ${colors.borderLight};
  border-radius: var(--radius);
  box-shadow: 0 -4px 12px rgba(0,0,0,0.12);
  overflow: hidden;
  z-index: 10;
`

const LitDropdownItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 0.6rem 0.9rem;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: ${colors.textLightMode};
  overflow: hidden;

  i { color: ${colors.primary}; flex-shrink: 0; }
  span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  &:hover { background: ${colors.backgroundAlt}; }
  & + & { border-top: 1px solid ${colors.borderLight}; }
`

const ProductFlyerModal: React.FC<ProductFlyerModalProps> = ({
  product: productProp,
  onClose,
  isAuthenticated,
  isAdmin = false,
  onLoginRequired,
  categoryImageMap = {},
}) => {
  const [activeProduct, setActiveProduct] = useState<CatalogProduct | null>(null)
  const product = activeProduct ?? productProp
  const navigate = useNavigate()

  useEffect(() => {
    setActiveProduct(null)
  }, [productProp])

  const { addItem } = useCart()
  const { data: featuredData } = usePublicFeaturedProducts()
  const featured = product ? (isFeaturedProduct(product) ? product : null) : null
  const productId = product ? (featured ? featured.product_id : product.id as number) : null
  const { data: literatureData } = usePublicLiteratureForProduct(productId ?? null)
  const { data: relatedData } = useRelatedProducts(productId ?? null)
  const litDocs = literatureData?.literature || []
  const relatedProducts = relatedData?.products || []
  const [isAdding, setIsAdding] = useState(false)
  const [activeLitDoc, setActiveLitDoc] = useState<ProductLiterature | null>(null)
  const [litDropdownOpen, setLitDropdownOpen] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  useEffect(() => {
    setActiveLitDoc(null)
    setImageIndex(0)
  }, [product])

  const featuredInfo = useMemo(() => {
    if (!featuredData?.products || !product) return null
    const pid = featured ? featured.product_id : product.id
    return featuredData.products.find(fp => fp.product_id === pid) ?? null
  }, [featuredData, product, featured])

  const catalogProduct = product
    ? isFeaturedProduct(product) ? product.product : product
    : null

  const productImages = useMemo(() => {
    const fallback = getProductImage(product?.image_url ?? null, product?.category ?? null, categoryImageMap)
    const imgs = [
      catalogProduct?.image_url || fallback,
      catalogProduct?.image_url_2,
      catalogProduct?.image_url_3,
    ].filter(Boolean) as string[]
    return imgs.length > 0 ? imgs : [fallback]
  }, [catalogProduct, product, categoryImageMap])

  const hasRealImages = productImages[0] !== PLACEHOLDER_IMAGE

  const displayPrice = catalogProduct?.sale_price ?? catalogProduct?.price ?? null
  const displayOriginalPrice = catalogProduct?.sale_price ? catalogProduct?.price ?? null : null
  const savings = displayPrice && displayOriginalPrice
    ? Math.round(((displayOriginalPrice - displayPrice) / displayOriginalPrice) * 100)
    : 0
  const displayDescription = featuredInfo?.custom_description || product?.description
  const displayPromoText = featuredInfo?.promo_text ?? null
  const displayCustomDetails = featuredInfo?.custom_details ?? null

  const customAttributes = product
    ? isFeaturedProduct(product)
      ? (product.product?.custom_attributes as Record<string, unknown> | null) ?? null
      : (product.custom_attributes as Record<string, unknown> | null) ?? null
    : null
  const customAttrEntries = customAttributes
    ? Object.entries(customAttributes).filter(([k, v]) => k && v != null && String(v).trim() !== '')
    : []

  const handleAddToCart = async () => {
    if (isAdmin || !product) return

    if (!isAuthenticated) {
      onLoginRequired?.()
      onClose()
      return
    }

    setIsAdding(true)
    await new Promise(resolve => setTimeout(resolve, 200))

    addItem({
      product_id: featured ? featured.product_id! : product.id as number,
      short_description: product.short_description || '',
      product_type: product.product_type || '',
      sku: product.sku || '',
      price: displayPrice || 0,
      sale_price: catalogProduct?.sale_price ? (catalogProduct.price ?? undefined) : undefined,
      image_url: product.image_url || (isFeaturedProduct(product) ? product.product?.category_image_url : product.category_image_url) || '',
      quantity: 1,
    })

    setIsAdding(false)
    onClose()
  }

  return (
    <>
      <AppModal
        show={!!product}
        onHide={onClose}
        title={product?.sku ?? ''}
        allowInteractionInDemo
        footer={
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {litDocs.length === 1 && (
                <WhiteButton style={{ flex: 1, justifyContent: 'center' }} onClick={() => setActiveLitDoc(litDocs[0])}>
                  <i className="bi bi-file-earmark-pdf" />
                  View Literature
                </WhiteButton>
              )}
              {litDocs.length > 1 && (
                <LitDropdownWrapper>
                  {litDropdownOpen && (
                    <>
                      <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onMouseDown={() => setLitDropdownOpen(false)} />
                      <LitDropdownMenu>
                        {litDocs.map((doc) => (
                          <LitDropdownItem key={doc.id} onClick={() => { setActiveLitDoc(doc); setLitDropdownOpen(false) }}>
                            <i className="bi bi-file-earmark-pdf" />
                            <span>{doc.title}</span>
                          </LitDropdownItem>
                        ))}
                      </LitDropdownMenu>
                    </>
                  )}
                  <WhiteButton style={{ width: '100%', justifyContent: 'center' }} onClick={() => setLitDropdownOpen(o => !o)}>
                    <i className="bi bi-file-earmark-pdf" />
                    View Literature ({litDocs.length})
                  </WhiteButton>
                </LitDropdownWrapper>
              )}
              <ActionButton
                onClick={handleAddToCart}
                disabled={isAdding || isAdmin}
                title={isAdmin ? 'Admins cannot place orders' : ''}
              >
                {isAdding ? (
                  <><Spinner animation="border" size="sm" /> Adding...</>
                ) : isAdmin ? (
                  <><i className="bi bi-lock"></i> Admin User</>
                ) : (
                  <><i className="bi bi-cart-plus"></i> {isAuthenticated ? 'Add to Cart' : 'Sign In'}</>
                )}
              </ActionButton>
            </div>
            {isAuthenticated && !isAdmin && product?.sku && (
              <div style={{ textAlign: 'right' }}>
                <OrderHistoryLink
                  onClick={() => { onClose(); navigate(`/customer-portal/orders?search=${encodeURIComponent(product.sku!)}`) }}
                >
                  <i className="bi bi-clock-history" /> View order history for this item
                </OrderHistoryLink>
              </div>
            )}
          </div>
        }
      >
        <>
          {product && (
            <div style={{ position: 'relative' }}>
              <ProductLayout>
                <ImageContainer>
                  {catalogProduct?.sale_price && <SaleCornerBadge large />}
                  {productImages.length > 1 && (
                    <CarouselArrow
                      className="prev"
                      onClick={() => setImageIndex(i => (i - 1 + productImages.length) % productImages.length)}
                      aria-label="Previous image"
                    >
                      <i className="bi bi-chevron-left" />
                    </CarouselArrow>
                  )}
                  <ProductImage
                    src={productImages[imageIndex]}
                    alt={product.product_type || ''}
                    onError={(e) => handleImageError(e, product.category, categoryImageMap)}
                    onClick={hasRealImages ? () => setLightboxIndex(imageIndex) : undefined}
                    style={hasRealImages ? { cursor: 'zoom-in' } : undefined}
                  />
                  {productImages.length > 1 && (
                    <CarouselArrow
                      className="next"
                      onClick={() => setImageIndex(i => (i + 1) % productImages.length)}
                      aria-label="Next image"
                    >
                      <i className="bi bi-chevron-right" />
                    </CarouselArrow>
                  )}
                  {productImages.length > 1 && (
                    <CarouselDots>
                      {productImages.map((_, i) => (
                        <Dot key={i} $active={i === imageIndex} onClick={() => setImageIndex(i)} style={{ cursor: 'pointer' }} />
                      ))}
                    </CarouselDots>
                  )}
                </ImageContainer>

                <ProductInfo>
                  {displayPrice && (
                    <PriceWrapper>
                      <Price>${displayPrice.toFixed(2)}</Price>
                      {displayOriginalPrice && (
                        <>
                          <OriginalPrice>${displayOriginalPrice.toFixed(2)}</OriginalPrice>
                          {savings > 0 && <SavingsBadge>Save {savings}%</SavingsBadge>}
                        </>
                      )}
                    </PriceWrapper>
                  )}

                  {product.manufacturer && (
                    product.manufacturer_image_url ? (
                      <img src={product.manufacturer_image_url} alt={product.manufacturer}
                        style={{ height: '2rem', width: 'auto', objectFit: 'contain', marginBottom: '0.75rem' }} />
                    ) : (
                      <MfrName>{product.manufacturer}</MfrName>
                    )
                  )}

                  {displayPromoText && (
                    <PromoBadge>{displayPromoText}</PromoBadge>
                  )}

                  {catalogProduct?.short_description && (
                    <Details style={{ marginBottom: '0.25rem' }}>{catalogProduct.short_description}</Details>
                  )}

                  <Description>{displayDescription}</Description>

                  {isFeaturedProduct(product) && product.details && (
                    <Details>{product.details}</Details>
                  )}

                  {displayCustomDetails && (
                    <Details style={{ marginTop: '0.75rem' }}>
                      <strong>Additional Details:</strong>
                      <div>{displayCustomDetails}</div>
                    </Details>
                  )}
                </ProductInfo>
              </ProductLayout>

              <SpecsGrid>
                {product.category && (
                  <SpecItem>
                    <i className="bi bi-tag"></i>
                    <strong>Category:</strong>
                    <span>{product.category}</span>
                  </SpecItem>
                )}
                {product.sku && (
                  <SpecItem>
                    <i className="bi bi-upc-scan"></i>
                    <strong>SKU:</strong>
                    <span>{product.sku}</span>
                  </SpecItem>
                )}
                {product.manufacturer && (
                  <SpecItem>
                    <i className="bi bi-building"></i>
                    <strong>Mfr:</strong>
                    <span>{product.manufacturer}</span>
                  </SpecItem>
                )}
                {customAttrEntries.map(([key, value]) => (
                  <SpecItem key={key}>
                    <i className="bi bi-info-circle"></i>
                    <strong>{key}:</strong>
                    <span>{String(value)}</span>
                  </SpecItem>
                ))}
              </SpecsGrid>

              {relatedProducts.length > 0 && (
                <RelatedSection>
                  <RelatedTitle>You May Also Like</RelatedTitle>
                  <RelatedGrid>
                    {relatedProducts.map(p => (
                      <RelatedCard key={p.id} onClick={() => setActiveProduct(p)}>
                        <img
                          src={getProductImage(p.image_url, p.category, categoryImageMap)}
                          alt={p.product_type || ''}
                          onError={(e) => handleImageError(e, p.category, categoryImageMap)}
                        />
                        <RelatedName>{p.product_type}</RelatedName>
                        {(p.sale_price ?? p.price) && (
                          <RelatedPrice>${(p.sale_price ?? p.price)!.toFixed(2)}</RelatedPrice>
                        )}
                      </RelatedCard>
                    ))}
                  </RelatedGrid>
                </RelatedSection>
              )}
            </div>
          )}
        </>
      </AppModal>

      <PdfPreviewModal
        show={!!activeLitDoc}
        title={activeLitDoc?.title ?? ''}
        url={activeLitDoc?.pdf_url ?? null}
        onHide={() => setActiveLitDoc(null)}
      />
      <ImageLightboxModal
        show={lightboxIndex !== null}
        images={productImages}
        initialIndex={lightboxIndex ?? 0}
        alt={product?.sku ?? ''}
        onHide={() => setLightboxIndex(null)}
      />
    </>
  )
}

export default ProductFlyerModal
